import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ColorConfiguration} from "./color-configuration";

@JsonObject()
export class VisualVariableLegendOptionsConfiguration {
    /**
     * Indicates whether to show the visual variable in the legend.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#legendOptions)
     */
    @JsonProperty()
    declare showLegend?: boolean;
    /**
     * The title describing the visualization of the visual variable in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#legendOptions)
     */
    @JsonProperty()
    declare title?: string;

}

@JsonObject()
export class VisualVariableConfiguration {
    /**
     * The name of the numeric attribute field that contains the data values used to determine the color/opacity/size/rotation of each feature.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#field)
     */
    @JsonProperty()
    declare field: string;
    /**
     * An object providing options for displaying the visual variable in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#legendOptions)
     */
    @JsonProperty()
    declare legendOptions: VisualVariableLegendOptionsConfiguration;
    /**
     * The visual variable type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#type)
     */
    @JsonProperty()
    declare type: "color" | "opacity" | "rotation" | "size";
    /**
     * An [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression following the specification defined by the [Arcade Visualization Profile](https://developers.arcgis.com/javascript/latest/arcade/#visualization).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#valueExpression)
     */
    @JsonProperty()
    declare valueExpression: string;
    /**
     * The title identifying and describing the associated [Arcade](https://developers.arcgis.com/javascript/latest/arcade/) expression as defined in the [valueExpression](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#valueExpression) property.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-VisualVariable.html#valueExpressionTitle)
     */
    @JsonProperty()
    declare valueExpressionTitle: string;

    constructor() {}
}

@JsonObject()
export class ColorVariableConfiguration extends VisualVariableConfiguration {
    /**
     * Name of the numeric attribute field by which to normalize the data.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-ColorVariable.html#normalizationField)
     */
    @JsonProperty()
    declare normalizationField: string;
    /**
     * An array of sequential objects, or stops, that defines a continuous color ramp.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-ColorVariable.html#stops)
     */
    @JsonProperty()
    declare stops: ColorStopConfiguration[];
    /**
     * The visual variable type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-ColorVariable.html#type)
     */
    @JsonProperty()
    declare readonly type: "color";

    constructor() {
        super();
    }
}

@JsonObject()
export class OpacityVariableConfiguration extends VisualVariableConfiguration {
    /**
     * Name of the numeric attribute field by which to normalize the data.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-OpacityVariable.html#normalizationField)
     */
    @JsonProperty()
    declare normalizationField: string;
    /**
     * An array of objects that defines the opacity to apply to features in a layer in a sequence of stops.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-OpacityVariable.html#stops)
     */
    @JsonProperty()
    declare stops: OpacityStopConfiguration[];
    /**
     * The visual variable type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-OpacityVariable.html#type)
     */
    @JsonProperty()
    declare readonly type: "opacity";

    constructor() {
        super();
    }

}

@JsonObject()
export class RotationVariableConfiguration extends VisualVariableConfiguration {
    /**
     * Only applicable when working in a [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * @default heading
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-RotationVariable.html#axis)
     */
    @JsonProperty()
    axis: "heading" | "tilt" | "roll";
    /**
     * Defines the origin and direction of rotation depending on how the angle of rotation was measured.
     *
     * @default geographic
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-RotationVariable.html#rotationType)
     */
    @JsonProperty()
    rotationType: "geographic" | "arithmetic";
    /**
     * The visual variable type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-RotationVariable.html#type)
     */
    @JsonProperty()
    declare readonly type: "rotation";

    constructor() {
        super();
    }
}

@JsonObject()
export class SizeVariableConfiguration extends VisualVariableConfiguration {
    /**
     * Only applicable when working in a [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * @default all
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#axis)
     */
    @JsonProperty()
    declare axis: "width" | "depth" | "height" | "width-and-depth" | "all";

    /**
     * The maximum data value used in the size ramp.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#maxDataValue)
     */
    @JsonProperty()
    declare maxDataValue: number;
    /**
     * The size used to render a feature containing the maximum data value.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#maxSize)
     */
    @JsonProperty()
    declare maxSize: number | ScaleDependentStopsConfiguration;
    /**
     * The minimum data value used in the size ramp.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#minDataValue)
     */
    @JsonProperty()
    declare minDataValue: number;
    /**
     * The size used to render a feature containing the minimum data value.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#minSize)
     */
    @JsonProperty()
    declare minSize: number | ScaleDependentStopsConfiguration;
    /**
     * The name of the numeric attribute field used to normalize the data in the given [field](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#field).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#normalizationField)
     */
    @JsonProperty()
    declare normalizationField: string;
    /**
     * An array of objects that defines the mapping of data values returned from [field](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#field) or [valueExpression](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#valueExpression) to icon sizes.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#stops)
     */
    @JsonProperty()
    declare stops: SizeStopConfiguration[];
    /**
     * This value must be `outline` when scaling polygon outline widths based on the view scale.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#target)
     */
    @JsonProperty()
    declare target: string;
    /**
     * The visual variable type.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#type)
     */
    @JsonProperty()
    declare readonly type: "size";
    /**
     * When setting a size visual variable on a renderer using an [ObjectSymbol3DLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-ObjectSymbol3DLayer.html), this property indicates whether to apply the value defined by the [height](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-ObjectSymbol3DLayer.html#height), [width](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-ObjectSymbol3DLayer.html#width), or [depth](https://developers.arcgis.com/javascript/latest/api-reference/esri-symbols-ObjectSymbol3DLayer.html#depth) properties to the corresponding [axis](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#axis) of this visual variable instead of proportionally scaling this axis' value.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#useSymbolValue)
     */
    @JsonProperty()
    declare useSymbolValue: boolean;
    /**
     * Specifies how to apply the data value when mapping real-world sizes.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#valueRepresentation)
     */
    @JsonProperty()
    declare valueRepresentation: "radius" | "diameter" | "area" | "width" | "distance";
    /**
     * Indicates the unit of measurement used to interpret the value returned by [field](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#field) or [valueExpression](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#valueExpression).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#valueUnit)
     */
    @JsonProperty()
    declare valueUnit:
    | "unknown"
    | "inches"
    | "feet"
    | "yards"
    | "miles"
    | "nautical-miles"
    | "millimeters"
    | "centimeters"
    | "decimeters"
    | "meters"
    | "kilometers"
    | "decimal-degrees";

    constructor() {
        super();
    }
}

@JsonObject()
export class SizeStopConfiguration {
    /**
     * A string value used to label the stop in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-SizeStop.html#label)
     */
    @JsonProperty()
    declare label: string;
    /**
     * The size value in points (between `0` and `90`) used to render features with the given [value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-SizeStop.html#value).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-SizeStop.html#size)
     */
    @JsonProperty()
    declare size: number;
    /**
     * Specifies the data value to map to the given [size](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-SizeStop.html#size).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-SizeStop.html#value)
     */
    @JsonProperty()
    declare value: number;

    constructor() {}
}

@JsonObject()
export class ScaleDependentStopsConfiguration {
    /**
     * Value must be `size`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#ScaleDependentStops)
     */
    @JsonProperty()
    declare type: string;
    /**
     * This value must be `$view.scale`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#ScaleDependentStops)
     */
    @JsonProperty()
    declare valueExpression: string;
    /**
     * **Deprecated since version 4.2. Use `valueExpression` instead**. The only supported expression is `view.scale`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#ScaleDependentStops)
     */
    @JsonProperty()
    declare expression?: string;
    /**
     * This value must be `outline` when scaling polygon outline widths based on the view scale.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#ScaleDependentStops)
     */
    @JsonProperty()
    declare target?: string;
    /**
     * An array of objects that define the size of the icon (or alternatively the width of the polygon outline) at the given scale `value`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-SizeVariable.html#ScaleDependentStops)
     */
    @JsonProperty()
    declare stops: SizeStopConfiguration[];

    constructor() {}
}

@JsonObject()
export class OpacityStopConfiguration {
    /**
     * A string value used to label the stop in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-OpacityStop.html#label)
     */
    @JsonProperty()
    declare label: string;
    /**
     * The opacity value (between `0.0` and `1.0`) used to render features with the given [value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-OpacityStop.html#value).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-OpacityStop.html#opacity)
     */
    @JsonProperty()
    declare opacity: number;
    /**
     * Specifies the data value to map to the given [opacity value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-OpacityStop.html#opacity).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-OpacityStop.html#value)
     */
    @JsonProperty()
    declare value: number;

    constructor() {}
}

@JsonObject()
export class ColorStopConfiguration {
    /**
     * The [Color](https://developers.arcgis.com/javascript/latest/api-reference/esri-Color.html) used to render features with the given [value](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-ColorStop.html#value).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-ColorStop.html#color)
     */
    @JsonProperty()
    declare color: ColorConfiguration;
    /**
     * A string value used to label the stop along the color ramp in the [Legend](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Legend.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-ColorStop.html#label)
     */
    @JsonProperty()
    declare label: string;
    /**
     * Specifies the data value to map to the given [color](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-ColorStop.html#color).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-renderers-visualVariables-support-ColorStop.html#value)
     */
    @JsonProperty()
    declare value: number;

    constructor() {}
}
